import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField,
    Tooltip, Typography,
} from '@mui/material';
import GlobalTip from '../../providers/GlobalTip';
import ContentReviewService from '../../services/ContentReviewService';
import SectionTitle from '../../components/common/SectionTitle';

/**
 * SPS-5480 A2 — Curated Content review.
 *
 * The one place a Support maintainer decides what the knowledge base may answer with.
 * Three decisions and no more: APPROVE (this may answer a merchant), EXCLUDE (this may
 * not, and here is why), REOPEN (I was wrong / it changed, look at it again). Everything
 * else on the page exists to find the rows that need one of the three.
 *
 * The page never computes review state. It shows what the API returns and re-reads after
 * every write, because the decision lives in the database and two people can work this
 * queue at the same time.
 */

const REVIEW_STATUSES = ['pending', 'approved', 'excluded'];

const EMPTY_FILTERS = {
    review_status: '',
    category: '',
    new_since_review: false,
    changed_since_review: false,
    q: '',
};

const EMPTY_SUMMARY = {
    total: 0, pending: 0, approved: 0, excluded: 0,
    new_since_review: 0, changed_since_review: 0, retrievable: 0,
};

const STATUS_COLOR = { approved: 'success', excluded: 'error', pending: 'default' };

export function formatDateTime(value) {
    if (!value) return '—';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function QueueBanner({ summary, loading }) {
    const waiting = summary.pending || 0;
    const changed = summary.changed_since_review || 0;
    const quiet = !loading && waiting === 0 && changed === 0;

    return (
        <Box
            data-testid="content-review-banner"
            sx={{
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                border: '1px solid', borderColor: quiet ? '#c7e3cd' : '#f3d9a4',
                bgcolor: quiet ? '#f2f9f3' : '#fffaf0',
                borderRadius: 2, px: 2, py: 1.5, mb: 3,
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {waiting} pending review
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {changed} changed upstream since review
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
                {summary.total || 0} documents · {summary.approved || 0} approved ·{' '}
                {summary.excluded || 0} excluded · {summary.retrievable || 0} reachable by chat
            </Typography>
        </Box>
    );
}

function ReasonDialog({ open, title, prompt, confirmLabel, onCancel, onConfirm, busy }) {
    const [reason, setReason] = useState('');

    useEffect(() => { if (open) setReason(''); }, [open]);

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>{prompt}</DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    minRows={3}
                    label="Reason"
                    inputProps={{ 'aria-label': 'Reason' }}
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                {/* The reason is not optional anywhere: the API rejects an exclusion
                    without one, and the button agrees with the API. */}
                <Button
                    variant="contained"
                    color="error"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => onConfirm(reason.trim())}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ContentReview() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [excludeTarget, setExcludeTarget] = useState(null);
    const [bulkAction, setBulkAction] = useState(null);
    const knownCategories = useRef([]);
    const TipContext = GlobalTip.useMessage();

    useEffect(() => { document.title = 'Retail ORBIT: Curated Content'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ContentReviewService.listDocuments({
                review_status: filters.review_status || undefined,
                category: filters.category || undefined,
                new_since_review: filters.new_since_review ? 1 : undefined,
                changed_since_review: filters.changed_since_review ? 1 : undefined,
                q: filters.q || undefined,
            });
            const data = Array.isArray(response?.data) ? response.data : [];
            setRows(data);
            setSummary({ ...EMPTY_SUMMARY, ...(response?.summary || {}) });

            // The category list is captured from an UNFILTERED read. Deriving it from the
            // current rows would make choosing a category delete every other option.
            if (!filters.category) {
                knownCategories.current = Array.from(
                    new Set(data.map((row) => row.category).filter(Boolean)),
                ).sort();
            }
        } catch (error) {
            setRows([]);
            setSummary(EMPTY_SUMMARY);
            TipContext.show(
                error?.response?.status === 403
                    ? 'Curated content review is limited to Support and Admin users.'
                    : 'Failed to load the curated content queue',
                'error',
            );
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (key) => (event) => setFilters((current) => ({
        ...current,
        [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
    }));

    // What a bulk approve would actually do, counted from the rows on screen. Excluded
    // rows are shown in the confirm rather than silently dropped: the maintainer should
    // see that the action is leaving somebody's decision alone.
    const pendingInView = useMemo(
        () => rows.filter((row) => row.review_status === 'pending').length,
        [rows],
    );
    const excludedInView = useMemo(
        () => rows.filter((row) => row.review_status === 'excluded').length,
        [rows],
    );

    const hasFilter = useMemo(
        () => Boolean(filters.review_status || filters.category || filters.new_since_review
            || filters.changed_since_review || filters.q),
        [filters],
    );

    const run = async (work, successMessage) => {
        setBusy(true);
        try {
            const result = await work();
            TipContext.show(
                typeof successMessage === 'function' ? successMessage(result) : successMessage,
                'success',
            );
            await load();
        } catch (error) {
            TipContext.show(
                error?.response?.data?.message || 'The review action failed; nothing was changed.',
                'error',
            );
        } finally {
            setBusy(false);
        }
    };

    const handleApprove = (row) => run(
        () => ContentReviewService.approve(row.id),
        `Approved “${row.title}” — it can now answer merchant questions.`,
    );

    const handleReopen = (row) => run(
        () => ContentReviewService.reopen(row.id),
        `Reopened “${row.title}” — it is back in the queue and unreachable by chat.`,
    );

    const handleExclude = (reason) => {
        const target = excludeTarget;
        setExcludeTarget(null);
        return run(
            () => ContentReviewService.exclude(target.id, reason),
            `Excluded “${target.title}”.`,
        );
    };

    const handleBulk = (reason) => {
        const action = bulkAction;
        setBulkAction(null);
        return run(
            () => ContentReviewService.bulk({ action, filters, reason }),
            (result) => {
                const applied = result?.applied ?? 0;
                const skipped = result?.skipped_excluded ?? 0;
                const verb = action === 'approve' ? 'Approved' : 'Excluded';
                return skipped > 0
                    ? `${verb} ${applied} · ${skipped} excluded left alone.`
                    : `${verb} ${applied} documents.`;
            },
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <SectionTitle label="Curated Content — knowledge base review" />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
                Every synced article arrives pending and unreachable. Approving one lets Indie answer with
                it; excluding one keeps it out permanently, through every later sync, with the reason on
                record. Upstream owns the words — this page owns whether a merchant ever sees them.
            </Typography>

            <QueueBanner summary={summary} loading={loading} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="content-review-status-label">Review status</InputLabel>
                    <Select
                        labelId="content-review-status-label"
                        label="Review status"
                        value={filters.review_status}
                        onChange={setFilter('review_status')}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        {REVIEW_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="content-review-category-label">Category</InputLabel>
                    <Select
                        labelId="content-review-category-label"
                        label="Category"
                        value={filters.category}
                        onChange={setFilter('category')}
                    >
                        <MenuItem value="">All categories</MenuItem>
                        {knownCategories.current.map((category) => (
                            <MenuItem key={category} value={category}>{category}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    label="Search titles"
                    value={filters.q}
                    onChange={setFilter('q')}
                    sx={{ minWidth: 200 }}
                />

                <FormControlLabel
                    control={<Switch checked={filters.new_since_review} onChange={setFilter('new_since_review')} />}
                    label="New since last review"
                />
                <FormControlLabel
                    control={(
                        <Switch
                            checked={filters.changed_since_review}
                            onChange={setFilter('changed_since_review')}
                        />
                    )}
                    label="Changed upstream since reviewed"
                />

                <Box sx={{ flexGrow: 1 }} />

                <Tooltip title={hasFilter ? '' : 'Filter the list first — bulk actions apply to the filtered set.'}>
                    <span>
                        <Button
                            variant="outlined"
                            color="success"
                            disabled={!hasFilter || busy || rows.length === 0}
                            onClick={() => setBulkAction('approve')}
                        >
                            Approve filtered
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title={hasFilter ? '' : 'Filter the list first — bulk actions apply to the filtered set.'}>
                    <span>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={!hasFilter || busy || rows.length === 0}
                            onClick={() => setBulkAction('exclude')}
                        >
                            Exclude filtered
                        </Button>
                    </span>
                </Tooltip>
                <Button onClick={() => setFilters(EMPTY_FILTERS)} disabled={!hasFilter}>Reset</Button>
            </Box>

            {loading ? (
                <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
            ) : rows.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    No documents match these filters.
                </Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                                {['Title', 'Category', 'Section', 'Source status', 'Review', 'Audience',
                                  'Chat', 'Last synced', 'Last changed by', 'Actions'].map((heading) => (
                                    <th
                                        key={heading}
                                        style={{ padding: '8px 12px', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}
                                    >
                                        {heading}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '8px 12px', maxWidth: 380 }}>
                                        <a href={row.canonical_url} target="_blank" rel="noreferrer">{row.title}</a>
                                        {row.changed_since_review && (
                                            <Chip size="small" label="changed upstream" color="warning"
                                                variant="outlined" sx={{ ml: 1 }} />
                                        )}
                                        {row.exclusion_reason && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Reason: {row.exclusion_reason}
                                            </Typography>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>{row.category || '—'}</td>
                                    <td style={{ padding: '8px 12px' }}>{row.section || '—'}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                        <Chip size="small" variant="outlined"
                                            color={row.status === 'active' ? 'default' : 'warning'}
                                            label={row.status} />
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>
                                        <Chip size="small" label={row.review_status}
                                            color={STATUS_COLOR[row.review_status] || 'default'} />
                                    </td>
                                    <td style={{ padding: '8px 12px' }}>{row.audience || '—'}</td>
                                    <td style={{ padding: '8px 12px' }}>{row.chat_enabled ? 'on' : 'off'}</td>
                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                        {formatDateTime(row.last_synced_at)}
                                    </td>
                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                        {row.last_changed_by === 'sync' ? 'sync' : row.last_changed_by || '—'}
                                        {row.reviewed_at && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                reviewed {formatDateTime(row.reviewed_at)}
                                            </Typography>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                                        <Button size="small" disabled={busy || row.review_status === 'approved'}
                                            onClick={() => handleApprove(row)}>
                                            Approve
                                        </Button>
                                        <Button size="small" color="error"
                                            disabled={busy || row.review_status === 'excluded'}
                                            onClick={() => setExcludeTarget(row)}>
                                            Exclude
                                        </Button>
                                        <Button size="small" color="inherit"
                                            disabled={busy || row.review_status === 'pending'}
                                            onClick={() => handleReopen(row)}>
                                            Reopen
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            )}

            <ReasonDialog
                open={Boolean(excludeTarget)}
                title={`Exclude “${excludeTarget?.title || ''}”`}
                prompt="Excluded documents stay out through every later sync. The reason is kept with the document."
                confirmLabel="Exclude"
                busy={busy}
                onCancel={() => setExcludeTarget(null)}
                onConfirm={handleExclude}
            />

            {bulkAction === 'exclude' ? (
                <ReasonDialog
                    open
                    title={`Exclude ${rows.length} documents`}
                    prompt={`This applies to all ${rows.length} documents matching the current filters.`}
                    confirmLabel={`Exclude ${rows.length}`}
                    busy={busy}
                    onCancel={() => setBulkAction(null)}
                    onConfirm={handleBulk}
                />
            ) : null}

            <Dialog open={bulkAction === 'approve'} onClose={() => setBulkAction(null)}>
                <DialogTitle>
                    {`Approve ${pendingInView} pending · ${excludedInView} excluded skipped`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`This approves the ${pendingInView} pending documents matching the current filters and lets `
                          + 'Indie answer with them.'}
                        {excludedInView > 0
                            ? ` The ${excludedInView} excluded documents in this filter are left as they are — `
                              + 'un-excluding one is a per-row decision: Reopen, then Approve.'
                            : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkAction(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        disabled={busy || pendingInView === 0}
                        onClick={() => handleBulk()}
                    >
                        {`Approve ${pendingInView}`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
